import polyUtil from 'polyline-encoded'
import { isArray, isObject } from '@/global/utils/utils'
import { ACTIVE_STATUS } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'

/**
 * The backend reports a winter service task nested - every codebook as its own
 * object, everything the vehicle's winter service configuration decides under
 * `vehicle`, the status as an `is_active` flag - while the form edits one flat field
 * per value. This is the single place that translation lives, so the listing, the
 * details tab and the create screen can never read or write a task differently.
 */

/**
 * The `is_active` flag as the status the dropdown and the table cell speak.
 */
export function toTaskStatus (record) {
  return record?.is_active === false ? ACTIVE_STATUS.inactive : ACTIVE_STATUS.active
}

/**
 * Backend record -> the flat keys the fields are bound to.
 *
 * Only the ids are editable. Everything the vehicle brings with it - its type, drive,
 * throw side, ownership, base, operator and subcontractor - is configured on the
 * vehicle itself, so it is shown as read-only text and never sent back.
 */
export function toTaskForm (record) {
  const {
    type,
    category,
    surface_type: surfaceType,
    cleaning_type: cleaningType,
    spread_type: spreadType,
    vehicle,
    driver
  } = record || {}

  return {
    status: toTaskStatus(record),
    type_id: type?.id ?? null,
    category_id: category?.id ?? null,
    surface_type_id: surfaceType?.id ?? null,
    cleaning_type_id: cleaningType?.id ?? null,
    spread_type_id: spreadType?.id ?? null,
    vehicle_id: vehicle?.id ?? null,
    driver_id: driver?.id ?? null,
    // Read-only, straight off the vehicle's winter service configuration.
    vehicle_type: vehicle?.type?.description ?? null,
    drive_type: vehicle?.drive_type?.description ?? null,
    throw_side: vehicle?.throw_side?.description ?? null,
    ownership: vehicle?.ownership?.description ?? null,
    base_name: vehicle?.base?.name ?? null,
    base_responsible_person: vehicle?.base?.responsible_person?.name ?? null,
    operator_name: vehicle?.operator?.name ?? null,
    subcontractor_name: vehicle?.subcontractor?.name ?? null
  }
}

/**
 * What the picked vehicle allows, read off a task the backend reported. The form needs
 * it to hold the spreading fields to the vehicle's configuration, and it is not part of
 * the task's own data - which is why it is read out separately from `toTaskForm`.
 *
 * The autocomplete answers with the same shape (see `useTaskVehicleFacts`), so the two
 * sources of a vehicle - a loaded task and a freshly picked registration - are one and
 * the same to everything downstream.
 */
export function toVehicleFacts (record) {
  const { vehicle } = record || {}

  if (!vehicle) {
    return null
  }

  return {
    vehicleId: vehicle.id ?? null,
    vehicleType: vehicle.type?.description ?? null,
    driveType: vehicle.drive_type?.description ?? null,
    throwSide: vehicle.throw_side?.description ?? null,
    ownership: vehicle.ownership?.description ?? null,
    // Every material the vehicle may spread - the task picks one out of these.
    spreadTypeIds: (isArray(vehicle.spread_types) ? vehicle.spread_types : [])
      .map(entry => Number(entry?.id))
      .filter(id => !!id),
    spreadCapacityMin: vehicle.spread_capacity_min ?? null,
    spreadCapacityMax: vehicle.spread_capacity_max ?? null,
    baseName: vehicle.base?.name ?? null,
    baseResponsiblePerson: vehicle.base?.responsible_person?.name ?? null,
    subcontractorName: vehicle.subcontractor?.name ?? null,
    operatorName: vehicle.operator?.name ?? null
  }
}

/**
 * Writes the flat keys onto the record itself. Content.vue hands `onDataReceived` the
 * very object it then turns into the form data, so the flat values have to land on it
 * rather than on a copy - that also keeps the untouched copy it restores on cancel
 * flat, instead of emptying every field.
 */
export function applyTaskForm (record) {
  if (!record) {
    return record
  }

  return Object.assign(record, toTaskForm(record))
}

/**
 * The same translation as a fresh object. Used after a save: the record Content.vue
 * already holds has been made reactive, so keys added to it afterwards would not be
 * picked up - it has to be handed a new object instead.
 */
export function withTaskForm (record) {
  return {
    ...record,
    ...toTaskForm(record)
  }
}

/**
 * Form values -> what the upsert endpoint accepts. The status dropdown edits what the
 * backend keeps as `is_active`, and the read-only vehicle facts have no place in a
 * payload at all - they belong to the vehicle, not to the task.
 */
export function toTaskPayload (data) {
  if (!data) {
    return data
  }

  const {
    status,
    vehicle_type: vehicleType,
    drive_type: driveType,
    throw_side: throwSide,
    ownership,
    base_name: baseName,
    base_responsible_person: baseResponsiblePerson,
    operator_name: operatorName,
    subcontractor_name: subcontractorName,
    ...payload
  } = data

  // Booleans are sent as 1/0 rather than true/false: the tab is posted as FormData and
  // Laravel's `boolean` rule rejects the string "true".
  if (payload.is_unplanned !== undefined) {
    payload.is_unplanned = payload.is_unplanned ? 1 : 0
  }

  if (status === undefined) {
    return payload
  }

  return {
    ...payload,
    is_active: status === ACTIVE_STATUS.inactive ? 0 : 1
  }
}

/**
 * A street's `path` as leaflet wants it: a flat list of `[lat, lng]` pairs.
 *
 * 2.0 stores the drawn shape as exactly that. 1.0 rows, which the same column still
 * holds, keep one or more encoded polylines instead - so all three shapes are read
 * here rather than at every call site.
 */
export function toPathLatLngs (path) {
  if (!path) {
    return []
  }

  if (typeof path === 'string') {
    return polyUtil.decode(path)
  }

  if (!isArray(path)) {
    return []
  }

  return path.reduce((points, entry) => {
    // Legacy: one encoded polyline per entry.
    if (typeof entry === 'string') {
      return points.concat(polyUtil.decode(entry))
    }

    if (isArray(entry) && entry.length >= 2) {
      return points.concat([[Number(entry[0]), Number(entry[1])]])
    }

    // A `{ lat, lng }` pair, which is what leaflet hands back after a draw.
    if (isObject(entry)) {
      const latitude = entry.lat ?? entry.latitude
      const longitude = entry.lng ?? entry.lon ?? entry.longitude

      if (latitude != null && longitude != null) {
        return points.concat([[Number(latitude), Number(longitude)]])
      }
    }

    return points
  }, [])
}
